import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Button,
  Tabs,
  Tab,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Icon } from "@iconify/react";
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { subscriptionCreateOrUpdate, subscriptionGetById, getFinancialSupportProducts, isOfferableFinancialProduct, filterSelectableUserPersonas } from '../subscriptionPlans.service';
import Loader from '../../../../components/loader';
import AlertToast from '../../../../components/alertToast';
import {
  createMenuProps,
  FieldLabel,
  formFieldSx,
  helperTextSx,
  pageCardSx as sharedPageCardSx,
  placeholderTextSx,
  sectionTitleSx,
} from '../../../../styles/formStyles';
import { largeMaterialIconSx, mediumMaterialIconSx, primaryIconColor } from '../../../../styles/iconStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../../styles/pageFormStyles';
import { getSentenceCaseFormatter } from '../../../../../helper/getSentenceCaseFormatter';
import { getAllcountryManagement } from '../../../countryManagement/countryManagement.service';
import InlineAlert from '../../../../components/inlineAlert';
import { resultMessage } from '../../../../../service/apiResult';
import { getAlluserType } from '../../../userManagement/userType/userType.service';

const fieldSx = formFieldSx;
const menuProps = createMenuProps({ sx: { overflowY: 'auto', width: 'auto' } });
const pageCardSx = { ...sharedPageCardSx, ...createEditCardSx };

// Reduce a dial/country code to bare digits so values like "+91", "91" and
// " +91 " all compare equal when matching a product's country to a country's phone code.
const toDialDigits = (value) => (value == null ? '' : value.toString().replace(/\D/g, ''));

const requiredNumberField = (requiredMessage, invalidMessage) => z.preprocess(
  (value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return undefined;
    }
    return Number(value);
  },
  z.number({ required_error: requiredMessage, invalid_type_error: invalidMessage })
    .finite({ message: invalidMessage })
    .nonnegative({ message: invalidMessage })
);

const optionalNumberField = (invalidMessage) => z.preprocess(
  (value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return undefined;
    }
    return Number(value);
  },
  z.number({ invalid_type_error: invalidMessage })
    .finite({ message: invalidMessage })
    .nonnegative({ message: invalidMessage })
    .optional()
);

const schema = z.object({
  planName: z.string().min(1, { message: 'Plan Name is required' }),
  userTypes: z.string().uuid({ message: 'Select a valid user type' }),
  country: z.string().uuid({ message: 'Select a valid country' }),
  description: z.string().min(1, { message: 'Description is required' }),
  monthlySetupFee: requiredNumberField('Monthly Setup Fee is required', 'Monthly Setup Fee must be a valid number'),
  monthlySubscriptionFee: requiredNumberField('Monthly Subscription Fee is required', 'Monthly Subscription Fee must be a valid number'),
  yearlySetupFee: requiredNumberField('Yearly Setup Fee is required', 'Yearly Setup Fee must be a valid number'),
  yearlySubscriptionFee: requiredNumberField('Yearly Subscription Fee is required', 'Yearly Subscription Fee must be a valid number'),
  monthlyPromotionCode: z.string().optional().or(z.literal('')),
  monthlyPromotionDiscount: optionalNumberField('Monthly Promotion Discount must be a valid number').or(z.literal('')),
  yearlyPromotionCode: z.string().optional().or(z.literal('')),
  yearlyPromotionDiscount: optionalNumberField('Yearly Promotion Discount must be a valid number').or(z.literal('')),
  monthlySetupFeeCurrency: z.string().min(1, { message: 'Monthly Setup Fee Currency is required' }),
  monthlySubscriptionFeeCurrency: z.string().min(1, { message: 'Monthly Subscription Fee Currency is required' }),
  yearlySetupFeeCurrency: z.string().min(1, { message: 'Yearly Setup Fee Currency is required' }),
  yearlySubscriptionFeeCurrency: z.string().min(1, { message: 'Yearly Subscription Fee Currency is required' }),
  features: z.array(z.object({ value: z.string().min(1, { message: 'Feature is required' }) })),
});

function SubcriptionsCreation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState("");
  const [saveError, setSaveError] = useState("");
  const [userTypeData, setUserTypeData] = useState([]);
  const [loadedUserType, setLoadedUserType] = useState(null);
  const [billingCycle, setBillingCycle] = useState('MONTHLY');
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [financialSupportProducts, setFinancialSupportProducts] = useState([]);
  const [monthlyProductsByType, setMonthlyProductsByType] = useState({});
  const [yearlyProductsByType, setYearlyProductsByType] = useState({});
  const [monthlySelectedTypes, setMonthlySelectedTypes] = useState(new Set());
  const [yearlySelectedTypes, setYearlySelectedTypes] = useState(new Set());
  const [monthlyNewType, setMonthlyNewType] = useState('');
  const [monthlyNewProduct, setMonthlyNewProduct] = useState('');
  const [yearlyNewType, setYearlyNewType] = useState('');
  const [yearlyNewProduct, setYearlyNewProduct] = useState('');

  const ALERT_DURATION = 2000;

  const { handleSubmit, formState: { errors, submitCount }, control, reset, setValue } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      planName: '',
      userTypes: '',
      country: '',
      description: '',
      monthlySetupFee: '',
      monthlySubscriptionFee: '',
      yearlySetupFee: '',
      yearlySubscriptionFee: '',
      monthlyPromotionCode: '',
      monthlyPromotionDiscount: '',
      yearlyPromotionCode: '',
      yearlyPromotionDiscount: '',
      monthlySetupFeeCurrency: '',
      monthlySubscriptionFeeCurrency: '',
      yearlySetupFeeCurrency: '',
      yearlySubscriptionFeeCurrency: '',
      features: [{ value: '' }, { value: '' }, { value: '' }, { value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'features',
  });

  // The plan's country scopes which financial support products are offered.
  // A product's country is derived from the Bank user who created it, exposed on
  // the product as `countryCode` (a dial code, e.g. "+91"/"+254"); we match it
  // against the selected country's phone code. Legacy products with no creator
  // country fall back to a currency match against the country's currency, so a
  // Kenya plan never lists India's products.
  const selectedCountryId = useWatch({ control, name: 'country' });
  const selectedCountry = useMemo(
    () => countryOptions.find((item) => item.id === selectedCountryId),
    [countryOptions, selectedCountryId],
  );
  const selectedCountryCurrency = (selectedCountry?.currencyCode || '').toUpperCase();
  const selectedCountryDialDigits = toDialDigits(selectedCountry?.phoneCode);
  // A country is chosen once we can scope by either signal.
  const hasCountryScope = Boolean(selectedCountryId) && (!!selectedCountryDialDigits || !!selectedCountryCurrency);

  const subscriptionPlanMetaData = useCallback(async () => {
    const res = await getAlluserType();
    if (Array.isArray(res?.data)) {
      setUserTypeData(filterSelectableUserPersonas(res.data));
      setSaveError('');
    } else {
      // Without user types the plan cannot be saved at all, so the reason is shown
      // rather than leaving an unexplained empty dropdown.
      setUserTypeData([]);
      setSaveError(resultMessage(res));
    }
    const products = await getFinancialSupportProducts();
    setFinancialSupportProducts(products);
  }, []);

  const loadCurrencies = useCallback(async () => {
    try {
      const pageSize = 100;
      let page = 0;
      let hasNextPage = true;
      const allCountries = [];

      while (hasNextPage) {
        const res = await getAllcountryManagement(page, pageSize);
        const countries = Array.isArray(res?.data?.content) ? res.data.content : [];
        allCountries.push(...countries);

        const totalPages = Number(res?.data?.totalPages || 0);
        if (totalPages > 0) {
          hasNextPage = page + 1 < totalPages;
        } else {
          // The save failed and the user is still sitting on unsaved input, so the reason
          // stays on the form until dismissed rather than being flashed and lost.
          setSaveError(resultMessage(res));
          setLoading(false);
        }

        page += 1;
        if (page > 100) {
          hasNextPage = false;
        }
      }

      const currencyMap = new Map();
      allCountries.forEach((country) => {
        const code = (country?.currencyCode || country?.currency || '').toString().trim();
        const symbol = (country?.currencySymbol || '').toString().trim();
        if (code && !currencyMap.has(code)) {
          currencyMap.set(code, symbol);
        }
      });

      const uniqueCurrencies = Array.from(currencyMap.entries())
        .map(([code, symbol]) => ({ code, symbol }))
        .sort((a, b) => a.code.localeCompare(b.code));

      setCurrencyOptions(uniqueCurrencies);

      const countries = allCountries
        .filter((country) => country?.id)
        .map((country) => ({
          id: country.id,
          name: (country?.name || country?.countryName || '').toString().trim(),
          currencyCode: (country?.currencyCode || country?.currency || '').toString().trim(),
          phoneCode: (country?.phoneCode || '').toString().trim(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setCountryOptions(countries);
    } catch {
      setCurrencyOptions([]);
      setCountryOptions([]);
    }
  }, []);

  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
    subscriptionPlanMetaData();
    loadCurrencies();
  }, [type, subscriptionPlanMetaData, loadCurrencies]);

  const fetchData = useCallback(async () => {
    const res = await subscriptionGetById(id);
    const subscriptionData = res;
    if (!subscriptionData) return;

    const pricingDetails = Array.isArray(subscriptionData?.pricingDetails) ? subscriptionData.pricingDetails : [];
    const promoCodes = Array.isArray(subscriptionData?.promoCodes) ? subscriptionData.promoCodes : [];
    const financialSupport = Array.isArray(subscriptionData?.financialSupportDetails) ? subscriptionData.financialSupportDetails : [];

    const monthlyPricingDetail = pricingDetails.find(p => p?.billingType === 'MONTHLY') || {};
    const yearlyPricingDetail = pricingDetails.find(p => p?.billingType === 'YEARLY') || {};
    const monthlyPromo = promoCodes.find(p => p?.billingType === 'MONTHLY') || {};
    const yearlyPromo = promoCodes.find(p => p?.billingType === 'YEARLY') || {};
    const monthlyFinancial = financialSupport.find(f => f?.billingType === 'MONTHLY') || {};
    const yearlyFinancial = financialSupport.find(f => f?.billingType === 'YEARLY') || {};

    // Populate financial support products from existing data
    const monthlyProductsByTypeData = {};
    const yearlyProductsByTypeData = {};
    const monthlyTypesSet = new Set();
    const yearlyTypesSet = new Set();

    if (monthlyFinancial?.financialSupportProductGroups) {
      monthlyFinancial.financialSupportProductGroups.forEach((group) => {
        if (group.productIds && group.productIds.length > 0) {
          monthlyProductsByTypeData[group.supportType] = group.productIds;
          monthlyTypesSet.add(group.supportType);
        }
      });
    }

    if (yearlyFinancial?.financialSupportProductGroups) {
      yearlyFinancial.financialSupportProductGroups.forEach((group) => {
        if (group.productIds && group.productIds.length > 0) {
          yearlyProductsByTypeData[group.supportType] = group.productIds;
          yearlyTypesSet.add(group.supportType);
        }
      });
    }

    setMonthlyProductsByType(monthlyProductsByTypeData);
    setYearlyProductsByType(yearlyProductsByTypeData);
    setMonthlySelectedTypes(monthlyTypesSet);
    setYearlySelectedTypes(yearlyTypesSet);
    setLoadedUserType(subscriptionData?.userType || null);

    const billingCycleMonths = Number(subscriptionData?.billingCycleMonths) || 1;
    if (billingCycleMonths === 12) {
      setBillingCycle('YEARLY');
    } else {
      setBillingCycle('MONTHLY');
    }

    reset((current) => ({
      ...current,
      planName: subscriptionData?.name || '',
      userTypes: subscriptionData?.userType?.id || '',
      country: subscriptionData?.countryId || '',
      description: (subscriptionData?.description || '').replace(/<[^>]*>/g, '').trim(),
      monthlySetupFee: subscriptionData?.setupFee ?? '',
      monthlySubscriptionFee: subscriptionData?.monthlyPrice ?? '',
      yearlySetupFee: subscriptionData?.yearlySetupFee ?? '',
      yearlySubscriptionFee: subscriptionData?.yearlyPrice ?? '',
      monthlyPromotionCode: monthlyPromo?.promoCode || '',
      monthlyPromotionDiscount: monthlyPromo?.discountAmount || '',
      yearlyPromotionCode: yearlyPromo?.promoCode || '',
      yearlyPromotionDiscount: yearlyPromo?.discountAmount || '',
      monthlySetupFeeCurrency: monthlyPricingDetail?.setupFeeCurrency || 'USD',
      monthlySubscriptionFeeCurrency: monthlyPricingDetail?.subscriptionFeeCurrency || 'USD',
      yearlySetupFeeCurrency: yearlyPricingDetail?.setupFeeCurrency || 'USD',
      yearlySubscriptionFeeCurrency: yearlyPricingDetail?.subscriptionFeeCurrency || 'USD',
      features: Array.isArray(subscriptionData?.features) && subscriptionData.features.length
        ? subscriptionData.features.map((f) => ({ value: (typeof f === 'string' ? f : f?.featureName) || '' }))
        : [{ value: '' }, { value: '' }, { value: '' }, { value: '' }],
    }));
  }, [id, reset, setValue]);

  useEffect(() => {
    if (type === 'edit') {
      fetchData();
    }
  }, [type, fetchData]);

  const handleAddFeature = () => append({ value: '' });
  const handleRemoveFeature = (index) => {
    remove(index);
  };

  const renderCurrencyAdornment = (fieldName, hasError = false) => (
    <InputAdornment position="end" sx={{ mr: -1 }}>
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            variant="standard"
            disableUnderline
            displayEmpty
            MenuProps={menuProps}
            IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="18" height="18" color={primaryIconColor} {...iconProps} />}
            renderValue={(selected) => {
              if (!selected) {
                return <Box component="span" sx={{ color: '#9E9E9E' }}>Select</Box>;
              }
              return selected;
            }}
            sx={{
              minWidth: 106,
              '& .MuiSelect-select': {
                py: 0,
                pr: '24px !important',
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                color: hasError ? '#B3261E' : '#1C1B1F',
                fontWeight: 400,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select currency
            </MenuItem>
            {currencyOptions.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.code} ({currency.symbol})
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </InputAdornment>
  );

  // Build the financial support groups for a billing cycle. Includes both the
  // products already committed to chips AND any product still selected in the
  // Support Type / Product dropdowns that the user did not "Add" yet, so a
  // selection visible in the dropdown is always persisted.
  const buildFinancialSupportGroups = (productsByType, pendingType, pendingProduct) => {
    const merged = {};
    Object.entries(productsByType).forEach(([supportType, productIds]) => {
      merged[supportType] = [...(productIds || [])];
    });

    if (pendingType && pendingProduct) {
      const existing = merged[pendingType] || [];
      if (!existing.includes(pendingProduct)) {
        merged[pendingType] = [...existing, pendingProduct];
      }
    }

    return Object.entries(merged)
      .map(([supportType, productIds]) => ({
        supportType,
        productIds: (productIds || []).filter(id => id && offerableProductIds.has(id)),
      }))
      .filter(group => group.productIds.length > 0);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const monthlyPricingPayload = {
        setupFee: Number(data.monthlySetupFee),
        subscriptionFee: Number(data.monthlySubscriptionFee),
        setupFeeCurrency: data.monthlySetupFeeCurrency,
        subscriptionFeeCurrency: data.monthlySubscriptionFeeCurrency,
      };

      const yearlyPricingPayload = {
        setupFee: Number(data.yearlySetupFee),
        subscriptionFee: Number(data.yearlySubscriptionFee),
        setupFeeCurrency: data.yearlySetupFeeCurrency,
        subscriptionFeeCurrency: data.yearlySubscriptionFeeCurrency,
      };

      if (data.monthlyPromotionCode?.trim()) {
        monthlyPricingPayload.promotionCode = data.monthlyPromotionCode.trim();
      }
      if (data.monthlyPromotionDiscount) {
        monthlyPricingPayload.promotionDiscount = data.monthlyPromotionDiscount;
      }

      const monthlyProducts = buildFinancialSupportGroups(monthlyProductsByType, monthlyNewType, monthlyNewProduct);
      if (monthlyProducts.length > 0) {
        monthlyPricingPayload.financialSupportProducts = monthlyProducts;
      }

      if (data.yearlyPromotionCode?.trim()) {
        yearlyPricingPayload.promotionCode = data.yearlyPromotionCode.trim();
      }
      if (data.yearlyPromotionDiscount) {
        yearlyPricingPayload.promotionDiscount = data.yearlyPromotionDiscount;
      }

      const yearlyProducts = buildFinancialSupportGroups(yearlyProductsByType, yearlyNewType, yearlyNewProduct);
      if (yearlyProducts.length > 0) {
        yearlyPricingPayload.financialSupportProducts = yearlyProducts;
      }

      const payload = {
        planName: data.planName,
        userType: data.userTypes || null,
        country: data.country || null,
        description: data.description,
        features: data.features
          .filter(f => f.value && f.value.trim())
          .map(f => ({ value: f.value.trim() })),
        pricing: {
          monthly: monthlyPricingPayload,
          yearly: yearlyPricingPayload,
        }
      };

      console.log('Submitting payload:', JSON.stringify(payload, null, 2));

      subscriptionCreateOrUpdate(payload, editMode, id).then((res) => {
        // The result is { success, data | error }; a refusal carries no top-level
        // `message`, so testing for one reported every failed save as a success and
        // navigated away from the unsaved form.
        if (!res.success) {
          setResponseMsg(resultMessage(res, 'An unexpected error occurred. Please try again.'));
          setAlert('false');
          setOpenAlert(true);
          setLoading(false);
          setTimeout(() => setAlert(''), ALERT_DURATION);
        } else {
          setResponseMsg('Subscription plan saved successfully.');
          setAlert('true');
          setOpenAlert(true);
          setLoading(false);
          setTimeout(() => {
            editMode ? navigate(`/subscriptionView/${id}?type=edit`) : navigate('/subscriptionPlans');
            setAlert('');
          }, ALERT_DURATION);
        }
      });
    } catch (error) {
      setLoading(false);
    }
  };

  const onInvalid = (formErrors) => {
    const yearlyFieldHasError = [
      'yearlySetupFee',
      'yearlySubscriptionFee',
      'yearlySetupFeeCurrency',
      'yearlySubscriptionFeeCurrency',
    ].some((field) => !!formErrors?.[field]);

    if (yearlyFieldHasError) {
      setBillingCycle('YEARLY');
      return;
    }

    const monthlyFieldHasError = [
      'monthlySetupFee',
      'monthlySubscriptionFee',
      'monthlySetupFeeCurrency',
      'monthlySubscriptionFeeCurrency',
    ].some((field) => !!formErrors?.[field]);

    if (monthlyFieldHasError) {
      setBillingCycle('MONTHLY');
    }
  };

  const handleBack = () => {
    editMode ? navigate(`/subscriptionView/${id}?type=edit`) : navigate('/subscriptionPlans');
  };

  const supportTypeOptions = [
    { value: 'CREDIT', label: 'Credit' },
    { value: 'GRANT_SUBSIDY', label: 'Grant/Subsidy' },
    { value: 'INVESTMENT', label: 'Investment' },
  ];

  const offerableProductIds = useMemo(
    () => new Set(financialSupportProducts.map((product) => product.id)),
    [financialSupportProducts]
  );

  const getProductsByType = (supportType) => {
    return financialSupportProducts.filter(p => {
      if (!isOfferableFinancialProduct(p)) return false;
      if (p.supportType !== supportType) return false;
      // Until a country is chosen there is nothing to scope against.
      if (!hasCountryScope) return false;
      // Prefer the product's own country (derived from its Bank creator). Fall
      // back to currency only for legacy products that have no creator country.
      const productDialDigits = toDialDigits(p.countryCode);
      if (productDialDigits) {
        return productDialDigits === selectedCountryDialDigits;
      }
      return (p.currency || '').toUpperCase() === selectedCountryCurrency;
    });
  };

  const getAvailableSupportTypes = () => {
    return supportTypeOptions.filter(typeOption =>
      getProductsByType(typeOption.value).length > 0
    );
  };

  const addProductToType = (supportType, productId, billingCycle) => {
    if (!supportType || !productId) return;
    const products = billingCycle === 'MONTHLY' ? monthlyProductsByType : yearlyProductsByType;
    const existing = products[supportType] || [];
    if (!existing.includes(productId)) {
      const updated = { ...products, [supportType]: [...existing, productId] };
      if (billingCycle === 'MONTHLY') {
        setMonthlyProductsByType(updated);
        setMonthlySelectedTypes(new Set([...monthlySelectedTypes, supportType]));
        setMonthlyNewProduct('');
      } else {
        setYearlyProductsByType(updated);
        setYearlySelectedTypes(new Set([...yearlySelectedTypes, supportType]));
        setYearlyNewProduct('');
      }
    }
  };

  const removeProductFromType = (supportType, productId, billingCycle) => {
    const products = billingCycle === 'MONTHLY' ? monthlyProductsByType : yearlyProductsByType;
    const remaining = (products[supportType] || []).filter(id => id !== productId);
    const updated = remaining.length > 0 ? { ...products, [supportType]: remaining } : { ...products };
    delete updated[supportType];

    if (billingCycle === 'MONTHLY') {
      setMonthlyProductsByType(updated);
      if (remaining.length === 0) {
        const newSelected = new Set(monthlySelectedTypes);
        newSelected.delete(supportType);
        setMonthlySelectedTypes(newSelected);
      }
    } else {
      setYearlyProductsByType(updated);
      if (remaining.length === 0) {
        const newSelected = new Set(yearlySelectedTypes);
        newSelected.delete(supportType);
        setYearlySelectedTypes(newSelected);
      }
    }
  };

  const feeFieldConfig = {
    MONTHLY: {
      setupFee: 'monthlySetupFee',
      subscriptionFee: 'monthlySubscriptionFee',
      setupCurrency: 'monthlySetupFeeCurrency',
      subscriptionCurrency: 'monthlySubscriptionFeeCurrency',
    },
    YEARLY: {
      setupFee: 'yearlySetupFee',
      subscriptionFee: 'yearlySubscriptionFee',
      setupCurrency: 'yearlySetupFeeCurrency',
      subscriptionCurrency: 'yearlySubscriptionFeeCurrency',
    },
  };

  const promotionalFieldConfig = {
    MONTHLY: {
      code: 'monthlyPromotionCode',
      discount: 'monthlyPromotionDiscount',
    },
    YEARLY: {
      code: 'yearlyPromotionCode',
      discount: 'yearlyPromotionDiscount',
    },
  };

  const monthlyRequiredFields = [
    'monthlySetupFee',
    'monthlySubscriptionFee',
    'monthlySetupFeeCurrency',
    'monthlySubscriptionFeeCurrency',
  ];
  const yearlyRequiredFields = [
    'yearlySetupFee',
    'yearlySubscriptionFee',
    'yearlySetupFeeCurrency',
    'yearlySubscriptionFeeCurrency',
  ];
  const showTabErrorIndicator = submitCount > 0;
  const hasMonthlyRequiredErrors = monthlyRequiredFields.some((field) => !!errors?.[field]);
  const hasYearlyRequiredErrors = yearlyRequiredFields.some((field) => !!errors?.[field]);

  const renderFeeFields = (cycle) => {
    const fields = feeFieldConfig[cycle];

    return (
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <FieldLabel label="Setup Fee" required />
          <Controller
            key={`${cycle}-setup-fee`}
            name={fields.setupFee}
            control={control}
            render={({ field, fieldState: { error } }) => (
              (() => {
                const currencyErrorMessage = errors?.[fields.setupCurrency]?.message;
                const feeErrorMessage = error?.message;
                return (
                  <TextField
                    {...field}
                    type="number"
                    placeholder="0"
                    fullWidth
                    error={!!feeErrorMessage || !!currencyErrorMessage}
                    helperText={feeErrorMessage || currencyErrorMessage || ''}
                    sx={{ ...fieldSx, ...helperTextSx }}
                    InputProps={{
                      endAdornment: renderCurrencyAdornment(fields.setupCurrency, !!currencyErrorMessage),
                    }}
                  />
                );
              })()
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <FieldLabel label="Subscription Fee" required />
          <Controller
            key={`${cycle}-subscription-fee`}
            name={fields.subscriptionFee}
            control={control}
            render={({ field, fieldState: { error } }) => (
              (() => {
                const currencyErrorMessage = errors?.[fields.subscriptionCurrency]?.message;
                const feeErrorMessage = error?.message;
                return (
                  <TextField
                    {...field}
                    type="number"
                    placeholder="0"
                    fullWidth
                    error={!!feeErrorMessage || !!currencyErrorMessage}
                    helperText={feeErrorMessage || currencyErrorMessage || ''}
                    sx={{ ...fieldSx, ...helperTextSx }}
                    InputProps={{
                      endAdornment: renderCurrencyAdornment(fields.subscriptionCurrency, !!currencyErrorMessage),
                    }}
                  />
                );
              })()
            )}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={handleBack}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems={'center'}>
          <Grid size={12}>
            <Typography
              className="app-page-title"
              sx={{
                ...createEditPageTitleSx,
                fontSize: { xs: 28, sm: 30, md: 34 },
                lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              }}
            >
              {editMode ? 'Update Subscription Plan' : 'Create Subscription Plan'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <InlineAlert message={saveError} onClose={() => setSaveError("")} variant="error" />

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="material-form">
        <Box sx={pageCardSx}>
          <Typography sx={sectionTitleSx}>Plan Details</Typography>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Plan Name" required />
              <Controller
                name="planName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} placeholder="Enter Plan Name" fullWidth error={!!error} helperText={error ? error.message : ''} sx={{ ...fieldSx, ...helperTextSx }} />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="User Types" required />
              <FormControl fullWidth error={!!errors.userTypes} sx={helperTextSx}>
                <Controller
                  name="userTypes"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      MenuProps={menuProps}
                      IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <Box sx={placeholderTextSx}>Select User Type</Box>;
                        }
                        const userType = userTypeData.find(item => item.id === selected)
                          || (loadedUserType?.id === selected ? loadedUserType : null);
                        return userType?.name ? getSentenceCaseFormatter(userType.name) : '';
                      }}
                      sx={fieldSx}
                    >
                      {userTypeData.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {getSentenceCaseFormatter(item.name)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {!!errors.userTypes && <FormHelperText error>{errors.userTypes.message}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Country" required />
              <FormControl fullWidth error={!!errors.country} sx={helperTextSx}>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      MenuProps={menuProps}
                      onChange={(event) => {
                        const nextCountryId = event.target.value;
                        field.onChange(nextCountryId);
                        // The available financial support products are scoped to the
                        // country's currency, so any pending dropdown selection made for
                        // the previous country is no longer valid here.
                        setMonthlyNewType('');
                        setMonthlyNewProduct('');
                        setYearlyNewType('');
                        setYearlyNewProduct('');
                        // Default the plan's currencies to the selected country's currency so
                        // the plan is priced in the country it is offered in.
                        const selected = countryOptions.find((item) => item.id === nextCountryId);
                        const currency = selected?.currencyCode;
                        if (currency) {
                          ['monthlySetupFeeCurrency', 'monthlySubscriptionFeeCurrency', 'yearlySetupFeeCurrency', 'yearlySubscriptionFeeCurrency']
                            .forEach((currencyField) => setValue(currencyField, currency, { shouldValidate: true }));
                        }
                      }}
                      IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <Box sx={placeholderTextSx}>Select Country</Box>;
                        }
                        const country = countryOptions.find(item => item.id === selected);
                        return country?.name || '';
                      }}
                      sx={fieldSx}
                    >
                      {countryOptions.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {!!errors.country && <FormHelperText error>{errors.country.message}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid size={12}>
              <FieldLabel label="Description" required />
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="Who is this plan for?"
                    fullWidth
                    multiline
                    minRows={3}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    sx={{
                      ...fieldSx,
                      ...helperTextSx,
                      '& .MuiOutlinedInput-root': {
                        ...fieldSx['& .MuiOutlinedInput-root'],
                        alignItems: 'flex-start',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4.5 }}>
            <Typography sx={sectionTitleSx}>Plan Features</Typography>
            <Grid container spacing={4}>
              {fields.map((item, index) => (
                <Grid key={item.id} size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label={`Feature ${index + 1}`} required />
                  <Controller
                    name={`features.${index}.value`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        placeholder="Enter Plan Feature"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...fieldSx, ...helperTextSx }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                type="button"
                                onClick={() => handleRemoveFeature(index)}
                                sx={{ minWidth: 'auto', p: 0, color: '#1C1B1F' }}
                              >
                                <Box component="span" className="material-symbols-rounded" sx={largeMaterialIconSx}>cancel</Box>
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
              <Button
                type="button"
                onClick={handleAddFeature}
                startIcon={<Box component="span" className="material-symbols-rounded" sx={mediumMaterialIconSx}>add</Box>}
                sx={{
                  minWidth: 144,
                  height: 32,
                  borderRadius: '6px',
                  border: '1px solid #76A312',
                  color: '#4F8506',
                  textTransform: 'none',
                  fontSize: 14,
                  lineHeight: '20px',
                  letterSpacing: '0.1px',
                  fontWeight: 600,
                }}
              >
                Add Feature
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 5 }}>
            <Tabs
              value={billingCycle}
              onChange={(_, next) => setBillingCycle(next)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 0,
                mb: 2.5,
                borderBottom: '1px solid #DFE5F6',
                '& .MuiTabs-flexContainer': {
                  gap: { xs: 2.5, sm: 4 },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#76A312',
                  height: 2,
                },
                '& .MuiTab-root': {
                  flexShrink: 0,
                  minHeight: 48,
                  pb: 1.5,
                },
              }}
            >
              <Tab
                value="MONTHLY"
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Monthly Fees</span>
                    {showTabErrorIndicator && hasMonthlyRequiredErrors && (
                      <Box component="span" sx={{ color: '#B3261E', fontWeight: 700 }}>*</Box>
                    )}
                  </Box>
                }
                sx={{
                  minHeight: 48,
                  px: 0,
                  textTransform: 'none',
                  fontSize: 16,
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  fontWeight: 500,
                  color: '#1C1B1F',
                  '&.Mui-selected': {
                    color: '#4F8506',
                  },
                }}
              />
              <Tab
                value="YEARLY"
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Yearly Fees</span>
                    {showTabErrorIndicator && hasYearlyRequiredErrors && (
                      <Box component="span" sx={{ color: '#B3261E', fontWeight: 700 }}>*</Box>
                    )}
                  </Box>
                }
                sx={{
                  minHeight: 48,
                  px: 0,
                  textTransform: 'none',
                  fontSize: 16,
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  fontWeight: 500,
                  color: '#1C1B1F',
                  '&.Mui-selected': {
                    color: '#4F8506',
                  },
                }}
              />
            </Tabs>

            <Box sx={{ pt: 2.5 }}>
              <Typography sx={sectionTitleSx}>Fee Details</Typography>
              <Box sx={{ display: billingCycle === 'MONTHLY' ? 'block' : 'none' }}>
                {renderFeeFields('MONTHLY')}
              </Box>
              <Box sx={{ display: billingCycle === 'YEARLY' ? 'block' : 'none' }}>
                {renderFeeFields('YEARLY')}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 4.5 }}>
            <Typography sx={sectionTitleSx}>Promotional Discount</Typography>
            <Box sx={{ display: billingCycle === 'MONTHLY' ? 'block' : 'none' }}>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Promo Code" />
                  <Controller
                    name={promotionalFieldConfig.MONTHLY.code}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} placeholder="e.g. SUMMER 2.0" fullWidth error={!!error} helperText={error ? error.message : ''} sx={{ ...fieldSx, ...helperTextSx }} />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Discount Percentage" />
                  <Controller
                    name={promotionalFieldConfig.MONTHLY.discount}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} type="number" placeholder="0" fullWidth error={!!error} helperText={error ? error.message : ''} sx={{ ...fieldSx, ...helperTextSx }} />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: billingCycle === 'YEARLY' ? 'block' : 'none' }}>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Promo Code" />
                  <Controller
                    name={promotionalFieldConfig.YEARLY.code}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} placeholder="e.g. SUMMER 2.0" fullWidth error={!!error} helperText={error ? error.message : ''} sx={{ ...fieldSx, ...helperTextSx }} />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Discount Percentage" />
                  <Controller
                    name={promotionalFieldConfig.YEARLY.discount}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} type="number" placeholder="0" fullWidth error={!!error} helperText={error ? error.message : ''} sx={{ ...fieldSx, ...helperTextSx }} />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ mt: 4.5 }}>
            <Typography sx={sectionTitleSx}>Financial Support Products</Typography>

            {!hasCountryScope && (
              <Typography sx={{ ...placeholderTextSx, mb: 2 }}>
                Select a country above to see the financial support products available in that country.
              </Typography>
            )}

            <Box sx={{ display: billingCycle === 'MONTHLY' ? 'block' : 'none' }}>
              <Grid container spacing={4} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Support Type" />
                  <FormControl fullWidth sx={helperTextSx}>
                    <Select
                      value={monthlyNewType}
                      onChange={(e) => {
                        setMonthlyNewType(e.target.value);
                        setMonthlyNewProduct('');
                      }}
                      displayEmpty
                      MenuProps={menuProps}
                      IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                      sx={fieldSx}
                      renderValue={(selected) => {
                        if (!selected) return <Box sx={placeholderTextSx}>Select support type</Box>;
                        const option = getAvailableSupportTypes().find(opt => opt.value === selected);
                        return option?.label || '';
                      }}
                    >
                      {getAvailableSupportTypes().map((item) => (
                        <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Product" />
                  <FormControl fullWidth sx={helperTextSx} disabled={!monthlyNewType}>
                    <Select
                      value={monthlyNewProduct}
                      onChange={(e) => setMonthlyNewProduct(e.target.value)}
                      displayEmpty
                      MenuProps={menuProps}
                      IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                      sx={fieldSx}
                      renderValue={(selected) => {
                        if (!selected) return <Box sx={placeholderTextSx}>Select product</Box>;
                        const product = financialSupportProducts.find(p => p.id === selected);
                        return product?.name || '';
                      }}
                    >
                      {monthlyNewType && getProductsByType(monthlyNewType).map((product) => (
                        <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Button
                    onClick={() => addProductToType(monthlyNewType, monthlyNewProduct, 'MONTHLY')}
                    disabled={!monthlyNewType || !monthlyNewProduct}
                    variant="outlined"
                    sx={{
                      borderColor: '#76A312',
                      color: '#4F8506',
                      textTransform: 'none',
                      '&:disabled': {
                        borderColor: '#DDD',
                        color: '#999',
                      }
                    }}
                  >
                    Add Product
                  </Button>
                </Grid>
              </Grid>

              {Array.from(monthlySelectedTypes).map((supportType) => (
                <Box key={supportType} sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {supportTypeOptions.find(o => o.value === supportType)?.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(monthlyProductsByType[supportType] || []).map((productId) => {
                      const product = financialSupportProducts.find(p => p.id === productId);
                      return product ? (
                        <Chip
                          key={productId}
                          label={product.name}
                          onDelete={() => removeProductFromType(supportType, productId, 'MONTHLY')}
                          sx={{ backgroundColor: '#F0F0F0' }}
                        />
                      ) : null;
                    })}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: billingCycle === 'YEARLY' ? 'block' : 'none' }}>
              <Grid container spacing={4} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Support Type" />
                  <FormControl fullWidth sx={helperTextSx}>
                    <Select
                      value={yearlyNewType}
                      onChange={(e) => {
                        setYearlyNewType(e.target.value);
                        setYearlyNewProduct('');
                      }}
                      displayEmpty
                      MenuProps={menuProps}
                      IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                      sx={fieldSx}
                      renderValue={(selected) => {
                        if (!selected) return <Box sx={placeholderTextSx}>Select support type</Box>;
                        const option = getAvailableSupportTypes().find(opt => opt.value === selected);
                        return option?.label || '';
                      }}
                    >
                      {getAvailableSupportTypes().map((item) => (
                        <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Product" />
                  <FormControl fullWidth sx={helperTextSx} disabled={!yearlyNewType}>
                    <Select
                      value={yearlyNewProduct}
                      onChange={(e) => setYearlyNewProduct(e.target.value)}
                      displayEmpty
                      MenuProps={menuProps}
                      IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                      sx={fieldSx}
                      renderValue={(selected) => {
                        if (!selected) return <Box sx={placeholderTextSx}>Select product</Box>;
                        const product = financialSupportProducts.find(p => p.id === selected);
                        return product?.name || '';
                      }}
                    >
                      {yearlyNewType && getProductsByType(yearlyNewType).map((product) => (
                        <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Button
                    onClick={() => addProductToType(yearlyNewType, yearlyNewProduct, 'YEARLY')}
                    disabled={!yearlyNewType || !yearlyNewProduct}
                    variant="outlined"
                    sx={{
                      borderColor: '#76A312',
                      color: '#4F8506',
                      textTransform: 'none',
                      '&:disabled': {
                        borderColor: '#DDD',
                        color: '#999',
                      }
                    }}
                  >
                    Add Product
                  </Button>
                </Grid>
              </Grid>

              {Array.from(yearlySelectedTypes).map((supportType) => (
                <Box key={supportType} sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {supportTypeOptions.find(o => o.value === supportType)?.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(yearlyProductsByType[supportType] || []).map((productId) => {
                      const product = financialSupportProducts.find(p => p.id === productId);
                      return product ? (
                        <Chip
                          key={productId}
                          label={product.name}
                          onDelete={() => removeProductFromType(supportType, productId, 'YEARLY')}
                          sx={{ backgroundColor: '#F0F0F0' }}
                        />
                      ) : null;
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ ...createEditFooterSx, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                onClick={() => navigate('/subscriptionPlans')}
                sx={{
                  ...createEditCancelButtonSx,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  ...createEditPrimaryButtonSx,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {editMode ? 'Update Plan' : 'Create Plan'}
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {loading && <Loader />}

      <Box textAlign={'end'}>
        <AlertToast open={openAlert && alert === 'true'} onClose={() => setAlert('')} message={responseMsg} variant="success" />
        <AlertToast open={openAlert && alert === 'false'} onClose={() => setAlert('')} message={responseMsg} variant="error" />
      </Box>
    </Box>
  );
}

export default SubcriptionsCreation;
